/**
 * Same stack as /qmenu-debug.html — isolate from React's npm video.js.
 */
const STACK_VERSION = "studio-qmenu-4";

function loadStyle(href) {
  const url = `${href}?v=${STACK_VERSION}`;
  if (document.querySelector(`link[data-cms-stack="${href}"]`)) {
    return Promise.resolve();
  }
  return new Promise((resolve, reject) => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = url;
    link.setAttribute("data-cms-stack", href);
    link.onload = () => resolve();
    link.onerror = () => reject(new Error(`CSS failed: ${href}`));
    document.head.appendChild(link);
  });
}

function loadScript(src) {
  const url = `${src}?v=${STACK_VERSION}`;
  document
    .querySelectorAll(`script[data-cms-stack="${src}"]`)
    .forEach((el) => el.remove());

  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = url;
    script.async = false;
    script.setAttribute("data-cms-stack", src);
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Script failed: ${src}`));
    document.head.appendChild(script);
  });
}

let stackPromise = null;

export function loadCmsPlayerStack() {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("window unavailable"));
  }

  if (
    window.__cmsVideojs &&
    window.__cmsVideojs.getPlugin &&
    window.__cmsVideojs.getPlugin("qualityMenu") &&
    window.__cmsVideojs.__studioStack === STACK_VERSION
  ) {
    window.videojs = window.__cmsVideojs;
    return Promise.resolve(window.__cmsVideojs);
  }

  if (stackPromise) return stackPromise;

  stackPromise = (async () => {
    await Promise.all([
      loadStyle("/dependencies/videojs/video-js.css"),
      loadStyle("/dependencies/videojs/videojs-quality-menu.css"),
    ]);

    // Wipe any prior global (npm / previous CMS) so quality-menu doesn't hit
    // "qualityLevels already exists" on a polluted instance.
    try {
      delete window.videojs;
    } catch (e) {
      window.videojs = undefined;
    }
    window.__cmsVideojs = null;

    await loadScript("/dependencies/videojs/video.js");
    if (!window.videojs) {
      throw new Error("CMS video.js did not set window.videojs");
    }
    if (window.videojs.getPlugin && window.videojs.getPlugin("qualityLevels")) {
      console.warn(
        "[cms-stack] qualityLevels present before quality-menu — unexpected"
      );
    }

    await loadScript("/dependencies/videojs/videojs-quality-menu.min.js");

    const videojs = window.videojs;
    if (!(videojs.getPlugin && videojs.getPlugin("qualityMenu"))) {
      throw new Error("qualityMenu not registered");
    }
    if (!(videojs.getPlugin && videojs.getPlugin("qualityLevels"))) {
      throw new Error("qualityLevels not registered");
    }

    videojs.__studioStack = STACK_VERSION;
    window.__cmsVideojs = videojs;
    window.videojs = videojs;
    return videojs;
  })().catch((err) => {
    stackPromise = null;
    throw err;
  });

  return stackPromise;
}

export function getVideojs() {
  return typeof window !== "undefined"
    ? window.__cmsVideojs || window.videojs || null
    : null;
}

/**
 * Convert Studio preview URL to Wowza SMIL adaptive master.
 *
 * API often returns (1 bitrate only):
 *   .../live3/f180-054a-.../playlist.m3u8
 *
 * ABR master (same as /qmenu-debug.html):
 *   .../live3/smil:f180-054a-....smil/playlist.m3u8
 */
export function ensureMasterPlaylist(url) {
  if (!url || typeof url !== "string") return url;
  const next = url.trim();
  const qIndex = next.search(/[?#]/);
  const query = qIndex >= 0 ? next.slice(qIndex) : "";
  const base = qIndex >= 0 ? next.slice(0, qIndex) : next;

  // Already SMIL → normalize to playlist.m3u8
  const smilIdx = base.toLowerCase().lastIndexOf(".smil");
  if (smilIdx !== -1) {
    const smilBase = base.slice(0, smilIdx + 5);
    return `${smilBase}/playlist.m3u8${query}`;
  }

  // .../live3/<streamId>/playlist.m3u8 or chunklist_*.m3u8
  // → .../live3/smil:<streamId>.smil/playlist.m3u8
  const singleBitrate = base.match(
    /^(https?:\/\/[^/]+\/live\d*\/)([^/]+)\/(?:playlist\.m3u8|chunklist_[^/]+\.m3u8)$/i
  );
  if (singleBitrate) {
    const [, prefix, streamId] = singleBitrate;
    if (!/^smil:/i.test(streamId)) {
      return `${prefix}smil:${streamId}.smil/playlist.m3u8${query}`;
    }
  }

  // .../live3/<streamId> (no file) → smil master
  const bareStream = base.match(/^(https?:\/\/[^/]+\/live\d*\/)([^/]+)\/?$/i);
  if (bareStream && !/^smil:/i.test(bareStream[2])) {
    return `${bareStream[1]}smil:${bareStream[2]}.smil/playlist.m3u8${query}`;
  }

  return next;
}

/** Count #EXT-X-STREAM-INF entries in a master playlist. */
export async function probeM3u8Variants(url) {
  const res = await fetch(url, { credentials: "omit" });
  if (!res.ok) {
    const err = new Error(`m3u8 HTTP ${res.status}`);
    err.status = res.status;
    throw err;
  }
  const text = await res.text();
  return (text.match(/#EXT-X-STREAM-INF/g) || []).length;
}
